/**
 * ACC (Autodesk Construction Cloud) Service
 *
 * Frontend service for ACC integration - handles OAuth flow,
 * project/folder browsing, and file linking.
 */

import { apiService } from './ApiService';

export interface ACCConnection {
  id: string;
  acc_account_id: string;
  acc_account_name: string;
  created_at: string;
  is_valid: boolean;
  scope_level?: 'readonly' | 'full';  // 'readonly' = can only browse/view, 'full' = can sync files
  // Token health tracking (for proactive keep-alive monitoring)
  refresh_token_updated_at?: string;
  days_since_refresh?: number;
  token_health?: 'healthy' | 'warning' | 'expired';  // healthy = <10 days, warning = 10-13 days, expired = 14+ days
}

export interface ACCHub {
  id: string;
  name: string;
  type: string;
}

export interface ACCProject {
  id: string;
  name: string;
}

export interface ACCFolderItem {
  id: string;
  name: string;
  type: 'folder' | 'file';
  urn: string | null;
  extension: string | null;
  createTime: string;
  lastModifiedTime: string;
}

export interface ACCViewable {
  guid: string;
  name: string;
  role: '3d' | '2d';
  viewableID?: string;
}

export interface ACCLevel {
  name: string;
  elevation: number;
  height?: number;
  isGroundPlane?: boolean;
  source?: string;
}

// NEW: File version information from ACC
export interface ACCFileVersion {
  versionNumber: number;
  versionId: string;
  versionUrn: string;
  derivativeUrn?: string | null;  // The actual URN for viewing (from relationships.derivatives.data.id)
  lastModifiedTime: string;
  lastModifiedUserName: string;
  fileSize?: number;
  extension?: string;
  storageId?: string;
  storageGuid?: string;
  publishType?: string;     // "Hybrid" or "Full" = published with downloadable content
  modelVersion?: number;    // Internal Revit model version
  isPublished?: boolean;    // True if this version was PUBLISHED, false if only SYNCED
}

export interface ACCLink {
  id: string;
  asset_id: string;
  /**
   * NULL once the Autodesk account is disconnected, or its owner deleted (migration 066).
   * The link and its settings are kept; it simply has no credential until it is re-pointed.
   * Previously this could not be null because the row was deleted outright instead.
   */
  acc_connection_id: string | null;
  acc_project_id: string;
  acc_project_name: string | null;
  acc_folder_id: string | null;
  acc_file_urn: string;
  acc_file_name: string;
  acc_file_version: number;
  last_sync_at: string | null;
  sync_status: 'pending' | 'syncing' | 'synced' | 'error';
  sync_error: string | null;
  sync_progress: number;
  created_at: string;
  updated_at: string;
  /** NULL when the link has no connection — what "needs reconnecting" is keyed off (066). */
  acc_account_name: string | null;
  // Pause/Resume sync (legacy)
  is_paused?: boolean;
  // Autodesk Viewer fields
  viewable_urn?: string;
  viewables_json?: ACCViewable[];
  conversion_type?: 'ifc' | 'svf2' | 'svf';
  // Building levels for camera spawn position
  levels_json?: ACCLevel[];
  default_level?: string | null;
  // Version selection fields (NEW)
  selected_version_number?: number | null;  // null = "Always Latest"
  selected_version_urn?: string | null;
  use_latest_version?: boolean;              // true = auto-sync enabled
  synced_version_number?: number | null;     // Actually synced version
  generate_master_views?: boolean;           // Enable Revit phases
  selected_viewable_guid?: string | null;    // Selected 3D view/phase GUID - shared across all users
  room_source_viewable_guid?: string | null; // Which viewable to load room geometry from
  room_source_viewable_name?: string | null; // Name of room source viewable for display
}

class ACCService {
  /**
   * Initiate OAuth flow - returns URL to redirect user to
   * @param assetId - The asset (site) ID - connections are per-site, not per-user
   * @param returnUrl - URL to redirect to after OAuth completes
   * @param scopeLevel - 'readonly' for minimal permissions (View only), 'full' for all permissions (default)
   */
  async initiateAuth(assetId: string, returnUrl: string = '/sites', scopeLevel: 'readonly' | 'full' = 'full'): Promise<{ authUrl: string; state: string }> {
    return apiService.post('/acc/auth', { assetId, returnUrl, scopeLevel });
  }

  /**
   * Get ACC connections available for an asset (site)
   * @param assetId - The asset (site) ID
   */
  async getConnections(assetId: string): Promise<ACCConnection[]> {
    return apiService.get(`/acc/connections?assetId=${assetId}`);
  }

  /**
   * Delete an ACC connection
   */
  async deleteConnection(connectionId: string): Promise<{ success: boolean }> {
    return apiService.delete(`/acc/connections/${connectionId}`);
  }

  /**
   * List hubs (Autodesk accounts) for a connection
   */
  async getHubs(connectionId: string): Promise<ACCHub[]> {
    return apiService.get(`/acc/hubs?connectionId=${connectionId}`);
  }

  /**
   * List projects in a hub
   */
  async getProjects(connectionId: string, hubId: string): Promise<ACCProject[]> {
    return apiService.get(`/acc/projects?connectionId=${connectionId}&hubId=${encodeURIComponent(hubId)}`);
  }

  /**
   * List folder contents
   * @param hubId - Required for top-level folders (when folderId is not provided)
   */
  async getFolderContents(connectionId: string, projectId: string, folderId?: string, hubId?: string): Promise<ACCFolderItem[]> {
    let url = `/acc/folders?connectionId=${connectionId}&projectId=${encodeURIComponent(projectId)}`;
    if (folderId) {
      url += `&folderId=${encodeURIComponent(folderId)}`;
    }
    if (hubId) {
      url += `&hubId=${encodeURIComponent(hubId)}`;
    }
    return apiService.get(url);
  }

  /**
   * List all versions of a file
   * @param connectionId - ACC connection ID
   * @param projectId - Project ID
   * @param itemId - File item ID
   * @returns Array of versions sorted by version number (newest first)
   */
  async getFileVersions(connectionId: string, projectId: string, itemId: string): Promise<ACCFileVersion[]> {
    return apiService.get(
      `/acc/versions?connectionId=${connectionId}&projectId=${encodeURIComponent(projectId)}&itemId=${encodeURIComponent(itemId)}`
    );
  }

  /**
   * Link an ACC file to an asset
   * @param fileItemId - The file item ID (used to get tip version for translation)
   * @param conversionType - 'ifc' to convert to IFC, 'svf2' for Autodesk Viewer (direct RVT)
   * @param selectedVersionNumber - null = "Always Latest" (auto-sync enabled), number = specific version (auto-sync disabled)
   * @param selectedVersionUrn - URN of the specific version if selected
   * @param generateMasterViews - Enable Revit phase generation (default true)
   */
  async linkFile(
    assetId: string,
    connectionId: string,
    projectId: string,
    projectName: string,
    folderId: string | null,
    fileItemId: string,
    fileName: string,
    conversionType: 'ifc' | 'svf2' = 'svf2',
    selectedVersionNumber: number | null = null,
    selectedVersionUrn: string | null = null,
    generateMasterViews: boolean = true
  ): Promise<{ success: boolean; linkId: string; message: string }> {
    return apiService.post('/acc/link', {
      assetId,
      connectionId,
      projectId,
      projectName,
      folderId,
      fileItemId,
      fileName,
      conversionType,
      selectedVersionNumber,
      selectedVersionUrn,
      generateMasterViews
    });
  }

  /**
   * Get ACC links for an asset
   */
  async getLinks(assetId: string): Promise<ACCLink[]> {
    return apiService.get(`/acc/links/${assetId}`);
  }

  /**
   * Delete an ACC link
   */
  async deleteLink(linkId: string): Promise<{ success: boolean }> {
    return apiService.delete(`/acc/links/${linkId}`);
  }

  /**
   * Manually trigger sync for a link
   */
  async triggerSync(linkId: string): Promise<{ success: boolean; message: string }> {
    return apiService.post(`/acc/sync/${linkId}`, {});
  }

  /**
   * Pause auto-sync for a link (disables webhook-triggered syncs)
   */
  async pauseSync(linkId: string): Promise<{ success: boolean; is_paused: boolean }> {
    return apiService.post(`/acc/links/${linkId}/pause`, {});
  }

  /**
   * Resume auto-sync for a link
   */
  async resumeSync(linkId: string): Promise<{ success: boolean; is_paused: boolean }> {
    return apiService.post(`/acc/links/${linkId}/resume`, {});
  }

  /**
   * Cancel an in-progress sync
   */
  async cancelSync(linkId: string): Promise<{ success: boolean; message?: string }> {
    return apiService.post(`/acc/links/${linkId}/cancel`, {});
  }

  /**
   * Use ACC derivatives directly instead of syncing (instant viewing)
   * This checks if ACC already has pre-translated derivatives and uses them directly.
   * Benefits: No download, no translation wait, instant viewing
   * Limitations: Requires ACC access, uses SVF format (not SVF2)
   */
  async useDirectDerivatives(linkId: string): Promise<{
    success: boolean;
    viewables?: ACCViewable[];
    region?: string;
    format?: string;
    error?: string;
    suggestion?: string;
  }> {
    return apiService.post(`/acc/links/${linkId}/use-direct`, {});
  }

  /**
   * View a file directly from ACC browser and create a link for future access
   * Checks for existing derivatives for the specified version and creates a link record
   * @param versionUrn - The specific version URN to view (from version selection)
   */
  async viewFileDirect(
    connectionId: string,
    projectId: string,
    projectName: string,
    itemId: string,
    fileName: string,
    assetId: string,
    folderId?: string | null,
    versionUrn?: string | null
  ): Promise<{
    success: boolean;
    urn?: string;
    viewables?: ACCViewable[];
    region?: string;
    format?: string;
    connectionId?: string;
    assetId?: string;
    linkId?: string;
    error?: string;
    suggestion?: string;
  }> {
    return apiService.post('/acc/view-direct', {
      connectionId,
      projectId,
      projectName,
      itemId,
      fileName,
      assetId,
      folderId,
      versionUrn
    });
  }

  /**
   * Check if a file is a supported Revit file
   */
  isRevitFile(item: ACCFolderItem): boolean {
    if (item.type !== 'file') return false;
    const ext = item.extension?.toLowerCase() || '';
    return ext.includes('rvt') || item.name.toLowerCase().endsWith('.rvt');
  }

  /**
   * Format sync status for display
   */
  formatSyncStatus(link: ACCLink): { label: string; className: string } {
    switch (link.sync_status) {
      case 'synced':
        return { label: 'Synced', className: 'acc-status-synced' };
      case 'syncing':
        return { label: 'Syncing...', className: 'acc-status-syncing' };
      case 'error':
        return { label: 'Error', className: 'acc-status-error' };
      case 'pending':
      default:
        return { label: 'Pending', className: 'acc-status-pending' };
    }
  }

  /**
   * Format last sync time
   */
  formatLastSync(link: ACCLink): string {
    if (!link.last_sync_at) return 'Never';
    const date = new Date(link.last_sync_at);
    return date.toLocaleString();
  }

  /**
   * Get access token for Autodesk Viewer
   * Site-specific: requires assetId for access verification
   */
  async getViewerToken(connectionId: string, assetId: string): Promise<{ access_token: string; expires_in: number }> {
    return apiService.get(`/acc/viewer-token?connectionId=${connectionId}&assetId=${assetId}`);
  }

  /**
   * Check if link is ready for Autodesk Viewer
   * Accepts both 'svf2' and 'svf' formats (svf is fallback for older Revit files)
   */
  isViewerReady(link: ACCLink): boolean {
    return link.sync_status === 'synced' &&
           (link.conversion_type === 'svf2' || link.conversion_type === 'svf') &&
           !!link.viewable_urn &&
           // A link with no connection has no credential to mint a viewer token with
           // (migration 066: disconnecting Autodesk now keeps the link instead of deleting
           // it). Without this the app would still offer the model, then fail at the token
           // call with "Authentication failed" — which names neither ACC nor the actual
           // remedy. Not ready is the honest answer, and it is the same answer the rest of
           // the app already knows how to render.
           !!link.acc_connection_id;
  }

  /**
   * Get viewables from link (parsed from JSON)
   */
  getViewables(link: ACCLink): ACCViewable[] {
    if (!link.viewables_json) return [];
    // Handle both string and parsed JSON
    if (typeof link.viewables_json === 'string') {
      try {
        return JSON.parse(link.viewables_json);
      } catch {
        return [];
      }
    }
    return link.viewables_json;
  }

  /**
   * Get building levels from link (parsed from JSON)
   */
  getLevels(link: ACCLink): ACCLevel[] {
    if (!link.levels_json) return [];
    // Handle both string and parsed JSON
    if (typeof link.levels_json === 'string') {
      try {
        return JSON.parse(link.levels_json);
      } catch {
        return [];
      }
    }
    return link.levels_json;
  }

  /**
   * Update default level for camera spawn position
   */
  async updateDefaultLevel(linkId: string, defaultLevel: string | null): Promise<{ success: boolean; default_level: string | null }> {
    return apiService.patch(`/acc/links/${linkId}`, { default_level: defaultLevel });
  }

  /**
   * Refresh building levels for camera spawn position without re-syncing the RVT
   */
  async refreshLevels(linkId: string): Promise<{ success: boolean; levels: ACCLevel[]; levelCount: number }> {
    return apiService.post(`/acc/links/${linkId}/refresh-levels`, {});
  }

  /**
   * Update version selection for an existing ACC link
   */
  async updateLinkVersion(linkId: string, selectedVersionNumber: number | null, selectedVersionUrn: string | null): Promise<{ success: boolean }> {
    const useLatestVersion = selectedVersionNumber === null;
    return apiService.patch(`/acc/links/${linkId}`, {
      selected_version_number: selectedVersionNumber,
      selected_version_urn: selectedVersionUrn,
      use_latest_version: useLatestVersion
    });
  }

  /**
   * Generic update for ACC link fields
   * Used for selected_viewable_guid and other updates
   */
  async updateLink(linkId: string, updates: Partial<Pick<ACCLink, 'default_level' | 'selected_viewable_guid' | 'room_source_viewable_guid' | 'room_source_viewable_name'>>): Promise<{ success: boolean; link: ACCLink }> {
    return apiService.patch(`/acc/links/${linkId}`, updates);
  }

  /**
   * Check if a sync appears stuck (no progress update for 15+ minutes)
   */
  isSyncStuck(link: ACCLink): boolean {
    if (link.sync_status !== 'syncing') return false;
    if (!link.updated_at) return false;

    const lastUpdate = new Date(link.updated_at);
    const minutesSinceUpdate = (Date.now() - lastUpdate.getTime()) / 1000 / 60;
    return minutesSinceUpdate >= 15;
  }

  /**
   * Get minutes since last update (for stuck syncs)
   */
  getMinutesSinceUpdate(link: ACCLink): number {
    if (!link.updated_at) return 0;
    const lastUpdate = new Date(link.updated_at);
    return Math.round((Date.now() - lastUpdate.getTime()) / 1000 / 60);
  }
}

// Singleton instance
export const accService = new ACCService();
